import React, { useState, useEffect } from "react";
import { useForm, ValidationError } from "@formspree/react";

export default function EventFormStep3({
  form,
  calculatedPrices,
  goPrevStep,
  showThankYouPage,
  setShowThankYouPage,
  resetForm,
  sendAnalyticsEvent,
  handleFormChange,
  showAlert
}) {
  // ===== CONFIGURATION =====
  const SUBMISSION_MODE = 'CUSTOM_BACKEND'; // 'FORMSPREE' | 'CUSTOM_BACKEND' | 'TESTING'
  
  // Formspree settings
  const FORMSPREE_ID = "xpwdnrgr"; // Ваш Formspree ID
  
  // Custom backend settings
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://server-event-calc-production.up.railway.app';
  
  // ===== STATE =====
  const [formspreeState, formspreeHandleSubmit] = useForm(
    SUBMISSION_MODE === 'FORMSPREE' ? FORMSPREE_ID : "test_form"
  );
  const [validationError, setValidationError] = useState("");
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasTrackedView, setHasTrackedView] = useState(false);

  // Auto-resize iframe height
  useEffect(() => {
    const updateHeight = () => {
      const height = document.documentElement.scrollHeight;
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ 
          type: 'resize', 
          height: height,
          step: 'step3' 
        }, '*');
      }
    };

    const timer = setTimeout(updateHeight, 200);
    return () => clearTimeout(timer);
  }, [validationError, apiError]);

  // Track when user reaches the results page
  useEffect(() => {
    if (!hasTrackedView && calculatedPrices.minTotal > 0) {
      setHasTrackedView(true);
      
      let sessionId = window.sessionStorage?.getItem('calc_session_id');
      if (!sessionId) {
        sessionId = 'calc_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        window.sessionStorage?.setItem('calc_session_id', sessionId);
      }

      sendAnalyticsEvent && sendAnalyticsEvent('price_calculation_completed', {
        event_type: form.eventType,
        category: form.category,
        guests_count: form.guests,
        children_count: form.eventType === "family" ? form.children : 0,
        separate_menu: form.eventType === "family" && form.children > 0 ? form.separateMenu : null,
        services_selected: form.services,
        services_count: form.services.length,
        price_min: calculatedPrices.minTotal,
        price_max: calculatedPrices.maxTotal,
        session_id: sessionId,
        timestamp: new Date().toISOString()
      });
    }
  }, [calculatedPrices, form, sendAnalyticsEvent, hasTrackedView]);

  // Handle Formspree success/error
  useEffect(() => {
    if (SUBMISSION_MODE === 'FORMSPREE') {
      if (formspreeState.succeeded && !showThankYouPage) {
        sendAnalyticsEvent && sendAnalyticsEvent('form_submission_success', {
          event_type: form.eventType,
          guests_count: form.guests,
          services_count: form.services.length,
          price_min: calculatedPrices.minTotal,
          price_max: calculatedPrices.maxTotal,
          session_id: window.sessionStorage?.getItem('calc_session_id'),
          submission_mode: 'formspree'
        });
        setShowThankYouPage(true);
        setIsSubmitting(false);
      }
      
      if (formspreeState.errors && formspreeState.errors.length > 0) {
        const errorMessage = formspreeState.errors
          .map(err => err.message || (err.field ? `${err.field}: ${err.code}` : err.code))
          .join(", ");
        setApiError(`Ошибка отправки: ${errorMessage}`);
        setIsSubmitting(false);
        
        sendAnalyticsEvent && sendAnalyticsEvent('form_submission_failed', {
          error: errorMessage,
          event_type: form.eventType,
          guests_count: form.guests,
          services_count: form.services.length,
          session_id: window.sessionStorage?.getItem('calc_session_id'),
          submission_mode: 'formspree'
        });
      }
    }
  }, [
    formspreeState.succeeded,
    formspreeState.errors,
    form.eventType,
    form.guests,
    form.services.length,
    calculatedPrices,
    sendAnalyticsEvent,
    showThankYouPage,
    setShowThankYouPage,
    SUBMISSION_MODE
  ]);

  const validateForm = () => {
    if (!form.name.trim()) {
      setValidationError("Введите ваше имя.");
      return false;
    }
    
    if (!form.phone.trim()) {
      setValidationError("Введите ваш телефон.");
      return false;
    }
    
    if (form.phone.trim().length < 7) {
      setValidationError("Номер телефона слишком короткий.");
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.email.trim() || !emailRegex.test(form.email)) {
      setValidationError("Введите корректный email.");
      return false;
    }
    
    return true;
  };

  const prepareFormData = () => {
    const familyServicesData = [
      { id: "fancy_picnic", name: "Фэнси-пикник с сервировкой" },
      { id: "poufs_zone", name: "Зона с пуфами" },
      { id: "mat_pillows", name: "Циновка и подушки" },
      { id: "catering", name: "Кейтеринг" },
      { id: "kids_menu", name: "Меню для детей" },
      { id: "bbq_chef", name: "Шашлычник на выезд" },
      { id: "drinks", name: "Напитки" },
      { id: "mobile_bar", name: "Выездной бар" },
      { id: "waiter", name: "Официант" },
      { id: "balloons", name: "Оформление шарами" },
      { id: "animator", name: "Аниматор" },
      { id: "photo", name: "Фотосъёмка" },
      { id: "video", name: "Видеоролик" },
      { id: "live_music", name: "Живая музыка" },
      { id: "dj", name: "Диджей" },
      { id: "venue_search", name: "Поиск площадки" }
    ];

    const b2bServicesData = [
      { id: "venue_rent", name: "Аренда помещения" },
      { id: "administration", name: "Администрирование и подготовка" },
      { id: "venue_search", name: "Поиск помещения" },
      { id: "catering", name: "Кейтеринг" },
      { id: "bar_service", name: "Барное обслуживание" },
      { id: "waiters", name: "Официанты" },
      { id: "registration", name: "Регистрация" },
      { id: "photo_zone", name: "Фотозона" },
      { id: "rollup", name: "Роллап" },
      { id: "badges", name: "Бейджи" },
      { id: "surface_branding", name: "Брендирование поверхностей" },
      { id: "branded_products", name: "Производство брендированной продукции" },
      { id: "equipment_rent", name: "Аренда оборудования (экран, проектор, звук, свет)" },
      { id: "photo", name: "Фотосъемка" },
      { id: "video", name: "Видеосъемка" },
      { id: "dj", name: "Диджей" }
    ];
    
    const servicesData = form.eventType === "b2b" ? b2bServicesData : familyServicesData;
    
    const servicesText = form.services
      .map(id => servicesData.find(service => service.id === id)?.name || id)
      .join(", ") || "Услуги не выбраны";

    return { servicesData, servicesText };
  };

  const submitToCustomBackend = async (formData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/submit-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Ошибка отправки формы');
      }

      return result;
    } catch (error) {
      console.error('Backend submission error:', error);
      throw error;
    }
  };

  const handleTestSubmit = async () => {
    console.log("=== TESTING MODE: Form Submission ===");
    console.log("Form Data:", form);
    console.log("Calculated Prices:", calculatedPrices);
    
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    sendAnalyticsEvent && sendAnalyticsEvent('form_submission_success', {
      event_type: form.eventType,
      guests_count: form.guests,
      services_count: form.services.length,
      price_min: calculatedPrices.minTotal,
      price_max: calculatedPrices.maxTotal,
      session_id: window.sessionStorage?.getItem('calc_session_id'),
      submission_mode: 'testing'
    });
    
    setShowThankYouPage(true);
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      showAlert && showAlert(validationError);
      return;
    }
    
    setValidationError("");
    setApiError("");
    setIsSubmitting(true);

    // Send analytics for form submission attempt
    sendAnalyticsEvent && sendAnalyticsEvent('form_submission_attempt', {
      event_type: form.eventType,
      guests_count: form.guests,
      services_count: form.services.length,
      price_min: calculatedPrices.minTotal,
      price_max: calculatedPrices.maxTotal,
      session_id: window.sessionStorage?.getItem('calc_session_id'),
      submission_mode: SUBMISSION_MODE.toLowerCase()
    });

    try {
      if (SUBMISSION_MODE === 'TESTING') {
        // ===== TESTING MODE =====
        await handleTestSubmit();
        
      } else if (SUBMISSION_MODE === 'CUSTOM_BACKEND') {
        // ===== CUSTOM BACKEND MODE =====
        const { servicesText } = prepareFormData();
        
        const customFormData = {
          name: form.name,
          phone: form.phone,
          email: form.email,
          event_type: form.eventType,
          category: form.category || "N/A",
          guests_count: form.guests,
          children_count: form.eventType === "family" ? form.children : 0,
          separate_menu: form.eventType === "family" && form.children > 0 
            ? (form.separateMenu ? "Да" : "Нет") 
            : "N/A",
          services: servicesText,
          services_ids: form.services,
          comment: form.comment || "Нет",
          price_min: calculatedPrices.minTotal,
          price_max: calculatedPrices.maxTotal,
          source: "Калькулятор мероприятий на сайте",
          session_id: window.sessionStorage?.getItem('calc_session_id')
        };
        
        await submitToCustomBackend(customFormData);
        
        console.log('Form successfully submitted to custom backend');
        
        sendAnalyticsEvent && sendAnalyticsEvent('form_submission_success', {
          event_type: form.eventType,
          guests_count: form.guests,
          services_count: form.services.length,
          price_min: calculatedPrices.minTotal,
          price_max: calculatedPrices.maxTotal,
          session_id: window.sessionStorage?.getItem('calc_session_id'),
          submission_mode: 'custom_backend'
        });
        
        setShowThankYouPage(true);
        
      } else if (SUBMISSION_MODE === 'FORMSPREE') {
        // ===== FORMSPREE MODE =====
        const { servicesText } = prepareFormData();
        
        const formspreeData = {
          _replyto: form.email,
          _subject: `Заявка с калькулятора от ${form.name} (${form.eventType})`,
          Имя_клиента: form.name,
          Телефон: form.phone,
          Email: form.email,
          Тип_события: form.eventType,
          Категория: form.category || "N/A",
          Количество_гостей: form.guests,
          Количество_детей: form.eventType === "family" ? form.children : "N/A",
          Отдельное_меню_дети: form.eventType === "family" && form.children > 0 
            ? (form.separateMenu ? "Да" : "Нет") 
            : "N/A",
          Выбранные_услуги: servicesText,
          Комментарий: form.comment || "Нет",
          Примерная_стоимость_Мин: calculatedPrices.minTotal,
          Примерная_стоимость_Макс: calculatedPrices.maxTotal,
          Источник: "Калькулятор мероприятий на сайте",
          Session_ID: window.sessionStorage?.getItem('calc_session_id')
        };

        await formspreeHandleSubmit(formspreeData);
        // Success/error handling is in useEffect above
      }
      
    } catch (error) {
      console.error('Form submission error:', error);
      
      let errorMessage = "Произошла ошибка при отправке формы. Попробуйте еще раз.";
      
      if (error.message.includes('fetch')) {
        errorMessage = "Не удается подключиться к серверу. Проверьте подключение к интернету.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setApiError(errorMessage);
      
      sendAnalyticsEvent && sendAnalyticsEvent('form_submission_failed', {
        error: error.message,
        event_type: form.eventType,
        guests_count: form.guests,
        services_count: form.services.length,
        session_id: window.sessionStorage?.getItem('calc_session_id'),
        submission_mode: SUBMISSION_MODE.toLowerCase()
      });
    } finally {
      if (SUBMISSION_MODE !== 'FORMSPREE') {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <form onSubmit={handleFormSubmit}>
      <h2>Результат</h2>
      <div className="price-block">
        <p className="price">
          Примерная стоимость:
          <strong>
            {" "}
            {calculatedPrices.minTotal}–{calculatedPrices.maxTotal} ₪
          </strong>
        </p>
        <p className="price-notice">
          Это ориентировочная цена. Итоговая стоимость может измениться в
          зависимости от конкретных требований.
        </p>
      </div>
      
      <h3>Контактная информация</h3>
      <div className="contact-form">
        <div className="form-field">
          <label htmlFor="name">Имя: <span className="required">*</span></label>
          <input
            id="name"
            name="name"
            type="text"
            value={form.name}
            onChange={handleFormChange}
            placeholder="Введите ваше имя"
            className={validationError.includes("имя") ? "error" : ""}
            required
          />
          {SUBMISSION_MODE === 'FORMSPREE' && (
            <ValidationError
              prefix="Имя"
              field="Имя_клиента"
              errors={formspreeState.errors}
              className="error-message formspree-error"
            />
          )}
        </div>
        
        <div className="form-field">
          <label htmlFor="phone">Телефон: <span className="required">*</span></label>
          <input
            id="phone"
            name="phone"
            type="tel"
            value={form.phone}
            onChange={handleFormChange}
            placeholder="Введите ваш номер телефона"
            className={validationError.includes("телефон") ? "error" : ""}
            required
          />
          {SUBMISSION_MODE === 'FORMSPREE' && (
            <ValidationError
              prefix="Телефон"
              field="Телефон"
              errors={formspreeState.errors}
              className="error-message formspree-error"
            />
          )}
        </div>
        
        <div className="form-field">
          <label htmlFor="email">Email: <span className="required">*</span></label>
          <input
            id="email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleFormChange}
            placeholder="Введите ваш email"
            className={validationError.includes("email") ? "error" : ""}
            required
          />
          {SUBMISSION_MODE === 'FORMSPREE' && (
            <ValidationError
              prefix="Email"
              field="_replyto"
              errors={formspreeState.errors}
              className="error-message formspree-error"
            />
          )}
        </div>
      </div>
      
      {validationError && (
        <div className="error-message main-error" role="alert">
          {validationError}
        </div>
      )}
      
      {apiError && (
        <div className="error-message main-error" role="alert">
          {apiError}
        </div>
      )}
      
      <div className="buttons">
        <button type="button" onClick={goPrevStep}>
          Назад
        </button>
        <button
          type="submit"
          disabled={isSubmitting || (SUBMISSION_MODE === 'FORMSPREE' && formspreeState.submitting)}
          className="submit-button"
        >
          {(isSubmitting || (SUBMISSION_MODE === 'FORMSPREE' && formspreeState.submitting))
            ? "Отправка..." 
            : "Отправить запрос"}
        </button>
      </div>

      
      <div className="contact-block" style={{ marginTop: "30px" }}>
        <div className="contact-label">Или свяжитесь с нами:</div>
        <div className="contact-phone">
          Телефон: <a href="tel:+972535934876">+972535934876</a>
        </div>
        <div className="contact-actions">
          <a
            className="contact-btn whatsapp"
            href="https://wa.me/972535934876"
            target="_blank"
            rel="noopener noreferrer"
          >
            WhatsApp
          </a>
          <a
            className="contact-btn instagram"
            href="https://www.instagram.com/minaker.events/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Instagram
          </a>
        </div>
      </div>
      
      <div className="buttons" style={{ marginTop: "20px", justifyContent: "center" }}>
        <button type="button" onClick={resetForm} className="secondary-button">
          Начать новый расчет
        </button>
      </div>
    </form>
  );
}